/**
 * Knowledge 输入契约（workbench knowledge 域）。
 *
 * 从 apps/workbench/src/app/knowledge/inputs.ts 抽离：RPC 契约层（packages/rpc-contract）
 * 与 serverFn/RPC 双入口共用同一份验证 schema（G3 SSOT）。workbench 侧保留
 * inputs.ts 薄转发（旧 handler 引用不变）。
 */
import { optionalWithDecodedDefault, requiredStringMax, withParse } from "@crm/effect/schema";
import { backgroundWriteProvenanceSchema } from "@crm/kernel/latest-write";
import { Schema, Struct } from "effect";

const nullableString = Schema.NullOr(Schema.String);
const semanticRevisionSchema = Schema.Finite.pipe(
  Schema.check(Schema.isInt()),
  Schema.check(Schema.isGreaterThanOrEqualTo(0)),
);

export const undefinedInputSchema = withParse(Schema.Undefined);

const idInputSchema = withParse(Schema.Struct({ id: Schema.String }));
const entryIdInputSchema = withParse(Schema.Struct({ entryId: Schema.String }));

export const getKnowledgeEntryByIdInputSchema = withParse(Schema.String);
export type GetKnowledgeEntryByIdInput = Schema.Schema.Type<
  typeof getKnowledgeEntryByIdInputSchema
>;

export const createManualDraftInputSchema = withParse(
  Schema.Struct({
    title: requiredStringMax("标题不能为空", 200, "标题最长 200 个字符"),
  }),
);
export type CreateManualDraftInput = Schema.Schema.Type<typeof createManualDraftInputSchema>;

export const reviewKnowledgeEntryInputSchema = withParse(
  Schema.Struct({
    entryId: Schema.String,
    action: Schema.Literals(["approve", "delete"]),
  }),
);
export type ReviewKnowledgeEntryInput = Schema.Schema.Type<typeof reviewKnowledgeEntryInputSchema>;

export const claimKnowledgeEntryInputSchema = entryIdInputSchema;
export const publishKnowledgeEntryInputSchema = entryIdInputSchema;
export const checkEntryDuplicatesInputSchema = entryIdInputSchema;

export const updateKnowledgeEntryInputSchema = withParse(
  Schema.Struct({
    id: Schema.String,
    changes: Schema.Struct({
      title: requiredStringMax("标题不能为空", 200, "标题最长 200 个字符"),
      content: nullableString,
    }).mapFields(Struct.map(Schema.optional)),
  }),
);
export type UpdateKnowledgeEntryInput = Schema.Schema.Type<typeof updateKnowledgeEntryInputSchema>;

export const saveKnowledgeEntrySnapshotInputSchema = withParse(
  Schema.Struct({
    id: Schema.String,
    expectedRevision: semanticRevisionSchema,
    provenance: backgroundWriteProvenanceSchema,
    changes: Schema.Struct({
      title: requiredStringMax("标题不能为空", 200, "标题最长 200 个字符"),
      content: nullableString,
    }).mapFields(Struct.map(Schema.optional)),
  }),
);
export type SaveKnowledgeEntrySnapshotInput = Schema.Schema.Type<
  typeof saveKnowledgeEntrySnapshotInputSchema
>;

export const deleteKnowledgeEntryInputSchema = idInputSchema;

export const getKnowledgeEntriesPaginatedInputSchema = withParse(
  Schema.Struct({
    status: Schema.Literals(["pending_review", "approved", "draft", "published"]),
    page: optionalWithDecodedDefault(Schema.Finite, () => 1),
    pageSize: optionalWithDecodedDefault(Schema.Finite, () => 20),
    search: Schema.optional(Schema.String),
    tagId: Schema.optional(Schema.String),
    courseId: Schema.optional(Schema.String),
  }),
);
export type GetKnowledgeEntriesPaginatedInput = Schema.Schema.Type<
  typeof getKnowledgeEntriesPaginatedInputSchema
>;

const entryCourseInputSchema = withParse(
  Schema.Struct({ entryId: Schema.String, courseId: Schema.String }),
);
export const addKnowledgeEntryCourseInputSchema = entryCourseInputSchema;
export const removeKnowledgeEntryCourseInputSchema = entryCourseInputSchema;
export const setKnowledgeEntryCoursesInputSchema = withParse(
  Schema.Struct({
    entryId: Schema.String,
    courseIds: Schema.Array(Schema.String),
  }),
);

const entryTagInputSchema = withParse(
  Schema.Struct({ entryId: Schema.String, tagId: Schema.String }),
);
export const addKnowledgeEntryTagInputSchema = entryTagInputSchema;
export const removeKnowledgeEntryTagInputSchema = entryTagInputSchema;
export const setKnowledgeEntryTagsInputSchema = withParse(
  Schema.Struct({
    entryId: Schema.String,
    tagIds: Schema.Array(Schema.String),
  }),
);

const knowledgeTagWriteFieldsSchema = Schema.Struct({
  name: requiredStringMax("标签名不能为空", 100, "标签名最长 100 个字符"),
  keywords: Schema.optional(Schema.NullOr(Schema.mutable(Schema.Array(Schema.String)))),
  color: Schema.optional(nullableString),
  sortOrder: Schema.optional(Schema.Finite),
});

export const createKnowledgeTagInputSchema = withParse(knowledgeTagWriteFieldsSchema);
export type CreateKnowledgeTagInput = Schema.Schema.Type<typeof createKnowledgeTagInputSchema>;

export const updateKnowledgeTagInputSchema = withParse(
  Schema.Struct({
    id: Schema.String,
    changes: knowledgeTagWriteFieldsSchema.mapFields(Struct.map(Schema.optional)),
  }),
);
export type UpdateKnowledgeTagInput = Schema.Schema.Type<typeof updateKnowledgeTagInputSchema>;

export const deleteKnowledgeTagInputSchema = idInputSchema;
